/**
 * Unverified Result Component
 *
 * Shows when insurance could not be verified
 * Allows user to review details and try again or proceed/skip
 * Supports multiple variants: "first" and "second" attempts
 */

"use client";

import { Button } from "@frontend/shared/components/ui/button";
import { motion } from "framer-motion";
import Image from "next/image";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "../insurance-connector-provider";

interface UnverifiedResultProps {
  isMobile?: boolean;
}

export const UnverifiedResult = ({
  isMobile = false,
}: UnverifiedResultProps) => {
  const t = useTranslations("InsuranceConnector");
  const send = useInsuranceConnectorSend();
  const context = useInsuranceConnectorContext();
  const state = useInsuranceConnectorState();

  // Determine variant based on current state
  const stateValue = state.value as { minimized?: string } | string;
  const currentState =
    typeof stateValue === "object" ? stateValue.minimized : "";
  const isSecondAttempt = currentState === "unverifiedSecondResult";
  const showProceedWithUnverified =
    isSecondAttempt && context.proceedWithUnverifiedInsurance;

  const containerClass = `rounded-3xl ${isMobile ? "w-full p-4" : "w-96 p-6"}`;

  // First attempt content
  if (!isSecondAttempt) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={containerClass}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-row justify-between items-start gap-2">
            <p className="text-2xl font-semibold">
              {t("UnverifiedFirstAttempt", {
                payer: context.primaryInsurance?.payer,
              })}
            </p>
            <Image
              src="/images/kiosk/Unverified.svg"
              alt={t("UnverifiedInsuranceAlt")}
              width={50}
              height={24}
            />
          </div>
          <p className="text-lg">{t("ReviewInsuranceDetails")}</p>
          <div className="flex gap-2">
            <Button
              onClick={() => send({ type: "REVIEW_NOW" })}
              className="h-12 w-full rounded-full text-lg"
            >
              {t("ReviewNow")}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Second attempt - show different content based on config
  if (showProceedWithUnverified) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={containerClass}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-row justify-between items-start gap-2">
            <p className="text-2xl font-semibold">
              {t("UnverifiedNotCovered", {
                payer: context.primaryInsurance?.payer,
              })}
            </p>
            <Image
              src="/images/kiosk/Unverified.svg"
              alt={t("UnverifiedInsuranceAlt")}
              width={50}
              height={24}
            />
          </div>
          <p className="text-lg">{t("ContinueUnverifiedNote")}</p>
          <p className="text-lg">{t("PaymentLinkNote")}</p>
          <div className="flex gap-2">
            <Button
              onClick={() => send({ type: "PROCEED_WITH_UNVERIFIED" })}
              className="h-12 w-full rounded-full text-lg"
            >
              {t("ContinueBooking")}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Second attempt without proceed flag - just show continue without insurance
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={containerClass}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-row justify-between items-start gap-2">
          <p className="text-2xl font-semibold">
            {t("UnverifiedFirstAttempt", {
              payer: context.primaryInsurance?.payer,
            })}
          </p>
          <Image
            src="/images/kiosk/Unverified.svg"
            alt={t("UnverifiedInsuranceAlt")}
            width={50}
            height={24}
          />
        </div>
        <p className="text-lg">
          {context.contentOverrides?.unverifiedFinalStateContent
            ?.subtitleText ?? t("UnableToVerify")}
        </p>
        <div className="flex gap-2">
          <Button
            onClick={() => send({ type: "CONTINUE_WITHOUT_INSURANCE" })}
            className="h-12 w-full rounded-full text-lg"
          >
            {t("ContinueWithoutInsurance")}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
